import { marketIntelModule } from '@/rules/page-access'
import { maAuthenticationGuard, maAuthorizationGuard, maCountryGuard, maDateRangeGuard, maParamsQueryNormalizeGuard, maTaxonomyGuard } from '@/router/guards'
import { Param, Query, RouteName, TitleFont } from '@/base/constants/route'
import { PRODUCT_FAMILY, PRODUCTS } from '@/base/constants/authorization'

const marketIntelRoutes = {
  path: 'market-intelligence',
  name: RouteName.MARKET_INTELLIGENCE,
  meta: {
    productFamily: PRODUCT_FAMILY.MARKET,
    shortTitle: 'MARKET',
    icon: 'trend-up',
    parent: true,
    requiredProducts: [PRODUCTS.MARKET_ENTERPRISE, PRODUCTS.MARKET_PRO],
  },
  redirect: { name: RouteName.MARKET_INTELLIGENCE_ANALYSIS_APP_ANALYSIS },
  component: () => import('@/layouts/Dashboard.vue'),
  beforeEnter: maAuthenticationGuard,
  children: [
    {
      path: 'analysis',
      name: RouteName.MARKET_INTELLIGENCE_ANALYSIS,
      meta: {
        title: 'ANALYSIS',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.MARKET_INTELLIGENCE_ANALYSIS_APP_ANALYSIS },
      children: [
        {
          path: 'app-analysis/:trackId?',
          name: RouteName.MARKET_INTELLIGENCE_ANALYSIS_APP_ANALYSIS,
          meta: {
            title: 'App Analysis',
            analyticsPageName: 'Market Intel - App Analysis',
            productFamily: PRODUCT_FAMILY.MARKET,
            ignoreChildrenFromMenu: true,
            requiredAccess: marketIntelModule.appAnalysisAccessRule,
          },
          component: () => import('@/pages/market/app-analysis/index.vue'),
          beforeEnter: maAuthorizationGuard,
          redirect: to => ({ name: RouteName.MARKET_INTELLIGENCE_ANALYSIS_APP_ANALYSIS_DOWNLOAD_ESTIMATION_TAB, params: to.params, query: to.query }),
          children: [
            {
              path: 'download-estimation',
              name: RouteName.MARKET_INTELLIGENCE_ANALYSIS_APP_ANALYSIS_DOWNLOAD_ESTIMATION_TAB,
              meta: {
                title: 'App Analysis',
                analyticsPageName: 'Market Intel - App Analysis - Download Estimation',
                productFamily: PRODUCT_FAMILY.MARKET,
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.COUNTRY_CODES,
                  Query.START_DATE,
                  Query.END_DATE,
                  Query.DEVICE,
                ],
              },
              component: () => import('@/pages/market/app-analysis/download-estimation/index.vue'),
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
            {
              path: 'revenue-estimation',
              name: RouteName.MARKET_INTELLIGENCE_ANALYSIS_APP_ANALYSIS_REVENUE_ESTIMATION_TAB,
              meta: {
                title: 'App Analysis',
                analyticsPageName: 'Market Intel - App Analysis - Revenue Estimation',
                productFamily: PRODUCT_FAMILY.MARKET,
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.COUNTRY_CODES,
                  Query.START_DATE,
                  Query.END_DATE,
                  Query.DEVICE,
                ],
              },
              component: () => import('@/pages/market/app-analysis/revenue-estimation/index.vue'),
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
            {
              path: 'daily-active-users',
              name: RouteName.MARKET_INTELLIGENCE_ANALYSIS_APP_ANALYSIS_DAU_TAB,
              meta: {
                title: 'App Analysis',
                analyticsPageName: 'Market Intel - App Analysis - Daily Active Users',
                productFamily: PRODUCT_FAMILY.MARKET,
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.COUNTRY_CODES,
                  Query.START_DATE,
                  Query.END_DATE,
                  Query.DEVICE,
                ],
              },
              component: () => import('@/pages/market/app-analysis/daily-active-users/index.vue'),
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
            {
              path: 'monthly-active-users',
              name: RouteName.MARKET_INTELLIGENCE_ANALYSIS_APP_ANALYSIS_MAU_TAB,
              meta: {
                title: 'App Analysis',
                analyticsPageName: 'Market Intel - App Analysis - Monthly Active Users',
                productFamily: PRODUCT_FAMILY.MARKET,
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.COUNTRY_CODES,
                  Query.START_DATE,
                  Query.END_DATE,
                  Query.DEVICE,
                ],
              },
              component: () => import('@/pages/market/app-analysis/monthly-active-users/index.vue'),
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
            {
              path: 'audience-geography',
              name: RouteName.MARKET_INTELLIGENCE_ANALYSIS_APP_ANALYSIS_AUDIENCE_GEOGRAPHY_TAB,
              meta: {
                title: 'App Analysis',
                analyticsPageName: 'Market Intel - App Analysis - Audience Geography',
                productFamily: PRODUCT_FAMILY.MARKET,
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.COUNTRY_CODES,
                  Query.START_DATE,
                  Query.END_DATE,
                  Query.DEVICE,
                ],
              },
              component: () => import('@/pages/market/app-analysis/audience-geography/index.vue'),
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
          ],
        },
        {
          path: 'store-analysis',
          name: RouteName.MARKET_INTELLIGENCE_ANALYSIS_STORE_ANALYSIS,
          meta: {
            title: 'Store Analysis',
            analyticsPageName: 'Market Intel - Store Analysis',
            productFamily: PRODUCT_FAMILY.MARKET,
            navigationWithoutAppTrack: true,
            requiredQueries: [
              Query.COUNTRY_CODES,
              Query.STORE,
              Query.CATEGORIES,
              Query.START_DATE,
              Query.END_DATE,
              Query.DEVICE,
            ],
            requiredAccess: marketIntelModule.storeAnalysisAccessRule,
          },
          component: () => import('@/pages/market/store-analysis/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
        },
        {
          path: 'publisher-analysis/:publisherId?',
          name: RouteName.MARKET_INTELLIGENCE_ANALYSIS_PUBLISHER_ANALYSIS,
          meta: {
            title: 'Publisher Analysis',
            analyticsPageName: 'Market Intel - Publisher Analysis',
            productFamily: PRODUCT_FAMILY.MARKET,
            navigationWithoutAppTrack: true,
            requiredQueries: [
              Query.COUNTRY_CODES,
              Query.STORE,
              Query.DEVICE,
              Query.START_DATE,
              Query.END_DATE,
              Query.CATEGORIES,
            ],
            requiredParams: [
              Param.PUBLISHER_ID,
            ],
            requiredAccess: marketIntelModule.publisherAnalysisAccessRule,
          },
          component: () => import('@/pages/market/publisher-analysis/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
      ],
    },
    {
      path: 'top-charts',
      name: RouteName.MARKET_INTELLIGENCE_TOP_CHARTS,
      meta: {
        title: 'TOP CHARTS',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.MARKET_INTELLIGENCE_TOP_CHARTS_TOP_APPS },
      children: [
        {
          path: 'top-apps',
          name: RouteName.MARKET_INTELLIGENCE_TOP_CHARTS_TOP_APPS,
          meta: {
            title: 'Top Apps',
            analyticsPageName: 'Market Intel - Top Apps',
            productFamily: PRODUCT_FAMILY.MARKET,
            navigationWithoutAppTrack: true,
            requiredQueries: [
              Query.COUNTRY_FILTER,
              Query.STORE,
              Query.DEVICE,
              Query.MONTH,
              Query.YEAR,
            ],
            requiredAccess: marketIntelModule.topAppsAccessRule,
          },
          component: () => import('@/pages/market/top-apps/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maTaxonomyGuard, maCountryGuard],
        },
        {
          path: 'top-publishers',
          name: RouteName.MARKET_INTELLIGENCE_TOP_CHARTS_TOP_PUBLISHERS,
          meta: {
            title: 'Top Publishers',
            analyticsPageName: 'Market Intel - Top Publishers',
            productFamily: PRODUCT_FAMILY.MARKET,
            navigationWithoutAppTrack: true,
            requiredQueries: [
              Query.COUNTRY_FILTER,
              Query.STORE,
              Query.DEVICE,
              Query.CATEGORY_ID,
              Query.MONTH,
              Query.YEAR,
            ],
            requiredAccess: marketIntelModule.topPublishersAccessRule,
          },
          component: () => import('@/pages/market/top-publishers/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
      ],
    },
  ],
}

export default marketIntelRoutes
